import { useState } from 'react';

import {
  Announcements,
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { Button } from '@/components/ui/button';
import { CatalogBuckets } from '@/modules/aiAgents/hooks/useModelStrategyCatalog';
import {
  emptyStep,
  MAX_STEPS_PER_CHAIN,
  ModelStrategyStep,
  stepSelectorKey,
} from '@/shared/types/modelStrategy';

import { ChainStepRow } from './ChainStepRow';

/** Selector keys used by every step except the one at `exceptIndex`. */
const usedKeysExcept = (steps: ModelStrategyStep[], exceptIndex: number): Set<string> => {
  const keys = new Set<string>();
  steps.forEach((step, i) => {
    if (i !== exceptIndex) {
      keys.add(stepSelectorKey(step));
    }
  });
  return keys;
};

type ChainEditorProps = {
  value: ModelStrategyStep[];
  buckets: CatalogBuckets;
  disabled?: boolean;
  /** Voice channel — single step only (no fallback). Adds latency warning when high reasoning is picked. */
  voice?: boolean;
  /** Header over the model column. Defaults to "Model". */
  label?: string;
  onChange: (next: ModelStrategyStep[]) => void;
};

// Stable client-side IDs let @dnd-kit track row identity across renders.
// They're owned by the editor and never sent over the wire.
const newId = () => uuidv4();

export const ChainEditor = ({
  value,
  buckets,
  disabled,
  voice = false,
  label,
  onChange,
}: ChainEditorProps) => {
  const { t } = useTranslation();

  const [ids, setIds] = useState<string[]>(() => value.map(newId));

  // Resync IDs when an external update changes the chain length (RTM, save
  // response, baseline reset). Setting state *during render* avoids the
  // double-render that an effect would cause — important here because dnd-kit
  // would otherwise briefly see `SortableContext items` misaligned with the
  // rendered rows. Same-length content swaps are rare enough that letting
  // IDs drift is acceptable; drag/keyboard ops align them in lockstep with
  // `onChange` below.
  if (ids.length !== value.length) {
    setIds(value.map(newId));
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const updateStep = (index: number, next: ModelStrategyStep) => {
    onChange(value.map((s, i) => (i === index ? next : s)));
  };

  const removeStep = (index: number) => {
    setIds((cur) => cur.filter((_, i) => i !== index));
    onChange(value.filter((_, i) => i !== index));
  };

  const addStep = () => {
    if (value.length >= MAX_STEPS_PER_CHAIN) {
      return;
    }
    setIds((cur) => [...cur, newId()]);
    onChange([...value, emptyStep()]);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) {
      return;
    }
    setIds((cur) => arrayMove(cur, from, to));
    onChange(arrayMove(value, from, to));
  };

  const announcements: Announcements = {
    onDragStart: ({ active }) => {
      const idx = ids.indexOf(String(active.id));
      return t('ai_agents.model_strategy.a11y.drag_start', { 0: idx + 1, 1: value.length });
    },
    onDragOver: ({ over }) => {
      if (!over) {
        return '';
      }
      const idx = ids.indexOf(String(over.id));
      return t('ai_agents.model_strategy.a11y.drag_over', { 0: idx + 1 });
    },
    onDragEnd: ({ over }) => {
      if (!over) {
        return t('ai_agents.model_strategy.a11y.drag_cancelled');
      }
      const idx = ids.indexOf(String(over.id));
      return t('ai_agents.model_strategy.a11y.drag_end', { 0: idx + 1 });
    },
    onDragCancel: () => t('ai_agents.model_strategy.a11y.drag_cancelled'),
  };

  // Reorder/remove controls only appear once there's more than one model:
  // a lone model has nothing to reorder and can't be removed. Voice is always
  // single-step, so it never shows them.
  const showControls = !voice && value.length > 1;

  // Voice is single-step: no fallbacks, no reorder, no add button. Every other
  // channel renders the same rows inside a sortable context with an add button.
  const rows = value.map((step, index) => (
    <ChainStepRow
      key={ids[index] ?? index}
      id={ids[index] ?? `tmp-${index}`}
      position={index}
      step={step}
      buckets={buckets}
      disabled={disabled}
      showRemove={showControls}
      showReorder={showControls}
      voiceWarning={voice && step.reasoning_effort === 'high'}
      usedKeys={voice ? undefined : usedKeysExcept(value, index)}
      onChange={(next) => updateStep(index, next)}
      onRemove={() => removeStep(index)}
    />
  ));

  // Headers live here, not in a sortable row, so the drag transform can't move
  // them. The trailing spacer mirrors the row's controls column for alignment; with a
  // single row there are no controls, so the fields run to the card's edge.
  const header = (
    <div className="flex items-start gap-2">
      <div className="flex flex-1 flex-col gap-2 sm:flex-row">
        <span className="flex-1 text-sm leading-5 font-medium text-foreground">
          {label ?? t('field.model')}
        </span>
        <span className="flex-1 text-sm leading-5 font-medium text-foreground">
          {t('ai_agents.model_strategy.reasoning_label')}
        </span>
      </div>
      {showControls && <span aria-hidden className="w-16 shrink-0" />}
    </div>
  );

  if (voice) {
    return (
      <div className="flex flex-col gap-1">
        {header}
        <div className="flex flex-col gap-2">{rows}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        {header}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToParentElement]}
          onDragEnd={handleDragEnd}
          accessibility={{
            announcements,
            screenReaderInstructions: {
              draggable: t('ai_agents.model_strategy.a11y.instructions'),
            },
          }}
        >
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <div className="flex flex-col gap-2">{rows}</div>
          </SortableContext>
        </DndContext>
      </div>

      <p className="text-sm leading-5 text-foreground-muted">
        {t('ai_agents.model_strategy.subtitle')}
      </p>

      {value.length < MAX_STEPS_PER_CHAIN && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={addStep}
          className="-ml-2 w-fit"
        >
          <PlusIcon />
          {t('ai_agents.model_strategy.add_fallback')}
        </Button>
      )}
    </div>
  );
};
